import React from "react";
import DOMPurify from "dompurify";
import {
  parseAIAssistantContent,
  ContentSegment,
} from "@/utils/aiAssistantRichContentParser";
import { AI_ASSISTANT_RICH_CONTENT_TAGS } from "@/config/aiAssistantRichContent";
import { AI_RICH_CONTENT_COMPONENTS } from "./AIRichContent";
import { generateHTML } from "@tiptap/core";
import { StarterKit } from "@tiptap/starter-kit";
import { Underline } from "@tiptap/extension-underline";
import { Link } from "@tiptap/extension-link";
import { Typography } from "@tiptap/extension-typography";
import { TextAlign } from "@tiptap/extension-text-align";
import { Highlight } from "@tiptap/extension-highlight";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { Color } from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import { Image } from "@tiptap/extension-image";
import { TaskList, TaskItem } from "@tiptap/extension-list";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";

interface AIContentRendererProps {
  content: any;
  className?: string;
  level?: number; // Add level prop for nesting depth
  brandColor?: string; // Add brand color prop
  schema?: any; // Optional JSON schema for ordering
  schemaPath?: string[]; // Path within the schema for nested ordering
}

/**
 * Renders AI Assistant generated content with rich content tag processing
 */
export const AIContentRenderer: React.FC<AIContentRendererProps> = ({
  content,
  className = "",
  level = 0, // Default to root level
  brandColor,
  schema,
  schemaPath = [],
}) => {
  // Helpers to get schema at current path and order keys accordingly
  const getSchemaAtPath = (rootSchema: any | undefined, path: string[]) => {
    if (!rootSchema) return undefined;
    try {
      let current: any = rootSchema;
      for (const part of path) {
        if (current && current.properties && current.properties[part]) {
          current = current.properties[part];
        } else {
          return current;
        }
      }
      return current;
    } catch {
      return undefined;
    }
  };

  const orderObjectEntries = (
    obj: Record<string, any>,
    currentSchema: any | undefined,
  ): [string, any][] => {
    const entries = Object.entries(obj);
    if (!currentSchema) return entries;
    const propOrder: string[] =
      currentSchema.required && Array.isArray(currentSchema.required)
        ? currentSchema.required
        : currentSchema.properties
          ? Object.keys(currentSchema.properties)
          : [];
    if (propOrder.length === 0) return entries;
    const indexOf = (k: string) => {
      const idx = propOrder.indexOf(k);
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };
    return entries.sort((a, b) => indexOf(a[0]) - indexOf(b[0]));
  };
  // Handle different content types
  const renderContent = () => {
    let processedContent = content;

    // Try to parse JSON strings that look like objects
    if (typeof content === "string" && content.trim().startsWith("{")) {
      try {
        processedContent = JSON.parse(content);
        console.log(
          "AIContentRenderer: Successfully parsed JSON string to object",
          processedContent,
        );
      } catch (error) {
        console.log(
          "AIContentRenderer: Failed to parse JSON string, treating as regular string",
          error,
        );
        // If parsing fails, treat as regular string
      }
    }

    // Render arrays explicitly to avoid showing numeric keys (0,1,2,...) and blocks
    if (Array.isArray(processedContent)) {
      const items = processedContent as unknown[];

      // If it's an array of strings, render as a clean list (no blocks)
      if (items.every((item) => typeof item === "string")) {
        const stripParagraphs = (html: string) =>
          html.replace(/<\/?p[^>]*>/g, "").trim();
        return (
          <ul
            className={`list-disc list-inside ${level === 0 ? "space-y-1" : "space-y-0.5"}`}
          >
            {(items as string[]).map((text, idx) => (
              <li key={idx} className="text-gray-700">
                {/* Apply basic formatting but avoid paragraph blocks inside list items */}
                <span
                  dangerouslySetInnerHTML={{
                    __html: stripParagraphs(processBasicFormatting(text)),
                  }}
                />
              </li>
            ))}
          </ul>
        );
      }

      // For arrays containing objects or mixed content, render items recursively without numbered headers
      return (
        <div className="space-y-2">
          {items.map((item, idx) => (
            <div key={idx}>
              <AIContentRenderer content={item} level={level + 1} />
            </div>
          ))}
        </div>
      );
    }

    if (typeof processedContent === "object" && processedContent !== null) {
      // TipTap JSON doc viewer
      if ((processedContent as any).type === "doc") {
        try {
          const html = generateHTML(processedContent as any, [
            StarterKit.configure({
              heading: { levels: [1, 2, 3, 4, 5, 6] },
              history: false,
              link: false,
            }),
            Typography,
            TextAlign.configure({ types: ["heading", "paragraph"] }),
            Subscript,
            Superscript,
            Underline,
            Color,
            TextStyle,
            TaskList,
            TaskItem.configure({ nested: true }),
            Highlight.configure({ multicolor: true }),
            Image,
            Link.configure({
              openOnClick: true,
              HTMLAttributes: {
                class: "text-blue-600 hover:underline hover:text-blue-800",
              },
            }),
            Table.configure({
              resizable: true,
              allowTableNodeSelection: false,
            }),
            TableRow,
            TableHeader,
            TableCell,
          ]);
          return (
            <div
              className="bg-white rounded-lg p-6 border border-gray-200 whitespace-pre-wrap ai-content-text prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
            />
          );
        } catch (e) {
          return (
            <pre className="text-sm text-gray-700 whitespace-pre-wrap bg-white rounded-lg p-6 border border-gray-200">
              {JSON.stringify(processedContent, null, 2)}
            </pre>
          );
        }
      }
      // Handle structured object content
      // At the top level, hide output_title so it doesn't render as a duplicate block under the heading
      let objectToRender: Record<string, any> = processedContent as Record<
        string,
        any
      >;
      if (
        level === 0 &&
        Object.prototype.hasOwnProperty.call(objectToRender, "output_title")
      ) {
        const { output_title, ...rest } = objectToRender;
        objectToRender = rest;
      }

      // Special case: wrapper object { output_title?, content: ... }
      // If the remaining root object is only { content: ... }, render the inner content directly (no master "Content" box)
      if (
        level === 0 &&
        Object.keys(objectToRender).length === 1 &&
        Object.prototype.hasOwnProperty.call(objectToRender, "content")
      ) {
        return (
          <AIContentRenderer
            content={(objectToRender as any).content}
            level={level}
            schema={schema}
            schemaPath={schemaPath}
          />
        );
      }

      const currentSchema = getSchemaAtPath(schema, schemaPath);
      const orderedEntries = orderObjectEntries(
        objectToRender as Record<string, any>,
        currentSchema,
      );
      return orderedEntries.map(([key, value]) => {
        const isNested = level > 0;
        const indentClass = isNested ? "ml-4" : "";
        const headerSize =
          level === 0 ? "text-base" : level === 1 ? "text-sm" : "text-xs";
        const bgColor = "bg-white";

        return (
          <div key={key} className={`mb-4 ${indentClass}`}>
            <button
              type="button"
              className={`w-full flex items-center gap-2 ${headerSize} font-semibold text-gray-900 capitalize mb-2 group`}
              onClick={(e) => {
                const content =
                  (e.currentTarget.nextSibling as HTMLElement) || null;
                if (content) {
                  content.classList.toggle("hidden");
                  const icon = e.currentTarget.querySelector(
                    "[data-collapsible-icon]",
                  ) as SVGElement | null;
                  if (icon) {
                    const isHidden = content.classList.contains("hidden");
                    icon.style.transform = isHidden
                      ? "rotate(0deg)"
                      : "rotate(90deg)";
                  }
                }
              }}
            >
              <svg
                className="h-4 w-4 text-gray-600 transition-transform duration-200"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                data-collapsible-icon
              >
                <path
                  d="M9 6l6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {key
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase())}
            </button>
            <div
              className={`${bgColor} rounded-lg p-3 border ${
                level === 0 ? "border-gray-200" : "border-gray-150"
              }`}
            >
              {typeof value === "string" ? (
                <AIContentRenderer
                  content={value}
                  level={level + 1}
                  schema={schema}
                  schemaPath={[...schemaPath, key]}
                />
              ) : typeof value === "object" &&
                value !== null &&
                !Array.isArray(value) ? (
                // Recursively render nested objects as separate blocks
                <AIContentRenderer
                  content={value}
                  level={level + 1}
                  schema={schema}
                  schemaPath={[...schemaPath, key]}
                />
              ) : (
                // Fallback for arrays and other types
                <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                  {JSON.stringify(value, null, 2)}
                </pre>
              )}
            </div>
          </div>
        );
      });
    }

    if (typeof processedContent === "string") {
      // Normalize escaped newlines before parsing - content from DB has literal \n characters
      const normalizeNewlines = (s: string) => s.split("\\n").join("\n");
      // Expand single-line flattened tables by converting "| |" row separators to newlines
      const expandFlatTables = (s: string) =>
        s
          .replace(
            /(^|\n)(\s*\|[^\n]+\|(?:\s*\|\s*\|[^\n]+\|)+)/g,
            (m, pre, tbl) => pre + tbl.replace(/\|\s*\|\s*/g, "|\n|"),
          )
          .replace(/\n\s*\|\s*\n/g, "\n");
      const normalizedContent = expandFlatTables(
        normalizeNewlines(processedContent),
      );

      // If we detect slide separators, split into multiple top-level slides
      const slideSeparator = /(?:<slide\s*\/>)|^\s*(?:[-_]{3,})\s*$/m;
      if (level === 0 && slideSeparator.test(normalizedContent)) {
        const rawSlides = normalizedContent
          .split(slideSeparator)
          .map((s) => s.trim())
          .filter(Boolean);
        return (
          <>
            {rawSlides.map((slide, i) => {
              const parsed = parseAIAssistantContent(slide);
              const inner = renderParsedContent(parsed.segments);
              return (
                <div
                  key={`slide-${i}`}
                  className="bg-white rounded-lg p-6 border border-gray-200 whitespace-pre-wrap ai-content-text prose prose-sm max-w-none mb-4"
                >
                  {inner}
                </div>
              );
            })}
          </>
        );
      }

      // Single block behavior: wrap content in white box but no master "Content" header
      const parsed = parseAIAssistantContent(normalizedContent);
      const inner = renderParsedContent(parsed.segments);
      if (level === 0) {
        return (
          <div className="bg-white rounded-lg p-6 border border-gray-200 whitespace-pre-wrap ai-content-text prose prose-sm max-w-none">
            {inner}
          </div>
        );
      }
      return inner;
    }

    // Fallback for other types
    return (
      <div className="text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-6 border">
        {String(processedContent)}
      </div>
    );
  };

  /**
   * Renders parsed content segments
   */
  const renderParsedContent = (segments: ContentSegment[]) => {
    return segments.map((segment, index) => {
      if (segment.type === "text") {
        // Process basic markdown-like formatting for text segments
        // Don't wrap in div to avoid block spacing between segments
        const html = processBasicFormatting(segment.content, {
          // If previous or next is a block, suppress extra gap
          suppressLeadingGap: index > 0 && segments[index - 1].type === "rich",
          suppressTrailingGap:
            index < segments.length - 1 && segments[index + 1].type === "rich",
        });

        // Return a fragment that will be rendered inline
        return (
          <span
            key={index}
            className="prose prose-sm max-w-none text-gray-700 leading-relaxed ai-content-text whitespace-pre-wrap inline-block w-full"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        );
      }

      if (segment.type === "rich" && segment.richType) {
        // Render rich content component
        const config = AI_ASSISTANT_RICH_CONTENT_TAGS[segment.richType];
        const ComponentName =
          config.component as keyof typeof AI_RICH_CONTENT_COMPONENTS;
        const Component = AI_RICH_CONTENT_COMPONENTS[ComponentName];

        if (Component) {
          return (
            <Component
              key={index}
              content={segment.content}
              className={segment.className}
            />
          );
        }

        // Fallback if component not found
        return (
          <div
            key={index}
            className={`p-3 rounded-lg border ${segment.className || "bg-gray-100"}`}
          >
            <strong>[{segment.richType}]</strong> {segment.content}
          </div>
        );
      }

      return null;
    });
  };

  /**
   * Process basic formatting like bold, italic, links, headings, lists, etc.
   */
  const processBasicFormatting = (
    text: string,
    opts?: { suppressLeadingGap?: boolean; suppressTrailingGap?: boolean },
  ): string => {
    // First, convert JSON arrays to bullet points
    const jsonArrayConverted = text.replace(
      /\[\s*(?:\n\s*)?"[^"]*"(?:\s*,\s*(?:\n\s*)?"[^"]*")*\s*(?:\n\s*)?\]/g,
      (match) => {
        try {
          // Try to parse as valid JSON first
          const parsed = JSON.parse(match);
          if (
            Array.isArray(parsed) &&
            parsed.every((item) => typeof item === "string")
          ) {
            return parsed.map((item) => `- ${item}`).join("\n");
          }
        } catch (e) {
          // If JSON parsing fails, use regex to extract quoted strings
          const quotedStrings = match.match(/"([^"]*)"/g);
          if (quotedStrings && quotedStrings.length > 0) {
            const items = quotedStrings.map((quoted) => quoted.slice(1, -1)); // Remove quotes
            return items.map((item) => `- ${item}`).join("\n");
          }
        }
        return match; // Return original if conversion fails
      },
    );

    // First, handle escaped newlines and clean excessive spacing
    // The content from DB has literal \n characters, not actual newlines
    const processedText = jsonArrayConverted
      .split("\\n")
      .join("\n") // Convert literal \n to actual newlines
      .replace(/\n{3,}/g, "\n\n") // Limit consecutive newlines to 2
      .replace(/(#{1,6}\s[^\n]+)\n(?!\n)/g, "$1\n\n"); // Ensure double newline after headers

    // Process existing HTML tags first (from AI output)
    let htmlText = processedText
      // Process existing <ul> and <ol> tags with their <li> items
      .replace(/<ul>(.*?)<\/ul>/gis, (match, content) => {
        const items = content.trim();
        return `<ul class="list-disc list-inside space-y-1 mb-2 ml-4">${items}</ul>`;
      })
      .replace(/<ol>(.*?)<\/ol>/gis, (match, content) => {
        const items = content.trim();
        return `<ol class="list-decimal list-inside space-y-1 mb-2 ml-4">${items}</ol>`;
      })
      // Convert standalone <li> tags (not already in ul/ol) to list items with wrapper
      .replace(/(?:<li>.*?<\/li>\s*)+/gs, (match, offset, str) => {
        // Check if this is already inside a ul/ol tag
        const beforeMatch = str.substring(Math.max(0, offset - 10), offset);
        const afterMatch = str.substring(
          offset + match.length,
          Math.min(str.length, offset + match.length + 10),
        );
        if (
          beforeMatch.includes("<ul>") ||
          beforeMatch.includes("<ol>") ||
          afterMatch.includes("</ul>") ||
          afterMatch.includes("</ol>")
        ) {
          return match; // Already in a list, don't wrap
        }
        const cleanedItems = match
          .replace(/<\/li>\s*<li>/g, "</li><li>") // Ensure proper separation
          .trim();
        // Check if items contain numbers at start, then use ol, otherwise ul
        const hasNumbers = /<li>\s*\d+\./.test(cleanedItems);
        const listType = hasNumbers ? "ol" : "ul";
        const listClass = hasNumbers
          ? "list-decimal list-inside space-y-1 mb-2 ml-4"
          : "list-disc list-inside space-y-1 mb-2 ml-4";
        return `<${listType} class="${listClass}">${cleanedItems}</${listType}>`;
      })
      // Add spacing to <li> items and remove leading numbers if present
      .replace(/<li>(\s*\d+\.\s*)?/gi, '<li class="mb-1">')
      // Process HTML strong/bold tags
      .replace(
        /<strong>(.*?)<\/strong>/gi,
        "<strong class='font-extrabold'>$1</strong>",
      )
      .replace(/<b>(.*?)<\/b>/gi, "<strong class='font-extrabold'>$1</strong>")
      // Process HTML em/italic tags
      .replace(/<em>(.*?)<\/em>/gi, "<em class='italic'>$1</em>")
      .replace(/<i>(.*?)<\/i>/gi, "<em class='italic'>$1</em>")
      // Clean up line breaks
      .replace(/<br\s*\/?>/gi, "\n");

    // Convert hyphen bullets before markdown formatting (robust, allows blanks and trailing '-')
    const convertHyphenLists = (s: string): string => {
      const lines = s.split("\n");
      const out: string[] = [];
      let items: string[] = [];
      const flush = () => {
        if (items.length) {
          out.push(
            `<ul class=\"list-disc list-inside space-y-1 mb-2 ml-4\">${items
              .map((it) => `<li class=\"mb-1\">${it}</li>`)
              .join("")}</ul>`,
          );
          items = [];
        }
      };
      for (const raw of lines) {
        const t = raw.trim();
        const isChecklist = /^-\s+\[(?: |x|X)\]/.test(t);
        const m = /^-\s+(.*)$/.exec(t);
        if (!isChecklist && m && m[1].trim().length > 0) {
          items.push(m[1]);
          continue;
        }
        if (items.length && (t === "" || t === "-")) continue;
        flush();
        out.push(raw);
      }
      flush();
      return out.join("\n");
    };

    htmlText = convertHyphenLists(htmlText);

    // Convert bullet character lists (• ) into proper <ul><li> lists
    const convertDotBulletLists = (s: string): string => {
      const lines = s.split("\n");
      const out: string[] = [];
      let items: string[] = [];
      const flush = () => {
        if (items.length) {
          out.push(
            `<ul class="list-disc list-inside space-y-1 mb-4 ml-4">${items
              .map((it) => `<li class=\"mb-1\">${it}</li>`)
              .join("")}</ul>`,
          );
          items = [];
        }
      };
      for (const raw of lines) {
        const t = raw.trim();
        const m = /^•\s+(.*)$/.exec(t);
        if (m && m[1].trim().length > 0) {
          items.push(m[1]);
          continue;
        }
        if (items.length && (t === "" || t === "•")) continue;
        flush();
        out.push(raw);
      }
      flush();
      return out.join("\n");
    };

    htmlText = convertDotBulletLists(htmlText);

    // Then process markdown formatting
    htmlText = htmlText
      // Headings (process first) - using existing ai-content-text classes
      .replace(/^### (.*$)/gm, "<h3>$1</h3>")
      .replace(/^## (.*$)/gm, "<h2>$1</h2>")
      .replace(/^# (.*$)/gm, "<h1>$1</h1>")

      // Process markdown-numbered lists only when HTML list tags are NOT present
      .replace(/(?:^(\d+)\.\s+(.+)$\n?)+/gm, (match) => {
        // If this block already includes HTML list tags, skip conversion to avoid nesting
        if (/<\/(?:li|ul|ol)>|<(?:li|ul|ol)>/i.test(match)) {
          return match;
        }

        const items = match
          .trim()
          .split("\n")
          .map((line) => {
            const m = line.match(/^(\d+)\.\s+(.+)$/);
            if (!m) return "";
            // Strip any stray <li> tags from the markdown line content
            const content = m[2].replace(/<li>/gi, "").replace(/<\/li>/gi, "");
            return `<li class="mb-1">${content}</li>`;
          })
          .filter(Boolean)
          .join("");
        return `<ol class="list-decimal list-inside space-y-1 mb-2 ml-4">${items}</ol>`;
      })

      // Bullet points (excluding checklist items) - tolerate a trailing hyphen-only line
      .replace(/(?:^- (?!\[[ x]\]) (.+)$\n?)+-?\s*$/gm, (match) => {
        const lines = match
          .split("\n")
          .map((l) => l.trim())
          .filter((l) => l.length > 0 && l !== "-");
        const items = lines
          .map((line) => {
            const m = line.match(/^- (?!\[[ x]\]) (.+)$/);
            return m ? `<li class="mb-1">${m[1]}</li>` : "";
          })
          .filter(Boolean)
          .join("");
        return items
          ? `<ul class="list-disc list-inside space-y-1 mb-2 ml-4">${items}</ul>`
          : "";
      })

      // Checklist items
      .replace(
        /^- \[ \] (.+)$/gm,
        '<div class="flex items-center gap-2 py-1"><input type="checkbox" class="rounded" /><span>$1</span></div>',
      )

      // Bold text (extra bold)
      .replace(/\*\*(.*?)\*\*/g, "<strong class='font-extrabold'>$1</strong>")

      // Italic text
      .replace(/\*(.*?)\*/g, "<em class='italic'>$1</em>")

      // Color codes with preview
      .replace(/(#[0-9A-Fa-f]{6}|#[0-9A-Fa-f]{3})/g, (match) => {
        return `<span class="inline-flex items-center gap-2">
            <span class="inline-block w-4 h-4 rounded border border-gray-300" style="background-color: ${match}"></span>
            <code class="bg-gray-100 px-1 rounded text-sm font-mono">${match}</code>
          </span>`;
      })

      // Links
      .replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        '<a href="$2" class="text-blue-600 hover:underline hover:text-blue-800 transition-colors" target="_blank" rel="noopener noreferrer">$1</a>',
      );

    // Optionally suppress leading/trailing newline gaps when adjacent to blocks
    if (opts?.suppressLeadingGap) {
      htmlText = htmlText.replace(/^(\n|\r\n)+/, "");
    }
    if (opts?.suppressTrailingGap) {
      htmlText = htmlText.replace(/(\n|\r\n)+$/, "");
    }

    // Collapse extra blank lines inside lists to avoid huge gaps between <li>
    htmlText = htmlText.replace(/(<(ul|ol)[^>]*>[\s\S]*?<\/\2>)/gi, (block) =>
      block.replace(/\n{2,}/g, "\n"),
    );

    // Slide splitting: only when explicit <slide/> markers are present
    const slideSeparator = /(?:<slide\s*\/>)|^(?:[-_]{3,})$/m;
    if (/<slide\s*\/>/.test(htmlText)) {
      const rawSlides = htmlText
        .split(slideSeparator)
        .map((s) => s.trim())
        .filter(Boolean);
      const slideHtml = rawSlides
        .map(
          (s) =>
            `<div class="bg-white rounded-lg p-6 border border-gray-200 mb-4">${s}</div>`,
        )
        .join("\n");
      htmlText = slideHtml;
    }

    // Remove standalone bullet-only lines that may slip through
    htmlText = htmlText.replace(/^\s*•\s*$/gm, "");

    // Preserve explicit double-newline boundaries as true double breaks
    // by splitting with a capturing group and emitting a spacing element
    // Normalize multiple blank lines to a single blank line to reduce large spacing
    htmlText = htmlText.replace(/\n{3,}/g, "\n\n");
    const parts = htmlText.split(/(\n\n)/);
    const isBlockStart = (s: string) => {
      const t = s.trim().toLowerCase();
      return /^<\s*(h[1-6]|ul|ol|li|p|blockquote|pre)(\s|>)/.test(t);
    };
    const isBlockEnd = (s: string) => {
      const t = s.trim().toLowerCase();
      // Consider a part ending with a closing block tag as a block end
      return /<\/(h[1-6]|ul|ol|li|p|blockquote|pre)\s*>\s*$/.test(t);
    };
    const wrappedContent = parts
      .map((part, idx) => {
        if (part === "\n\n") {
          const prev = parts[idx - 1] ?? "";
          const next = parts[idx + 1] ?? "";
          // Skip spacing if adjacent to block elements (e.g., headings, lists)
          // Also suppress if the previous non-empty part is a heading markdown line
          const prevTrim = (prev || "").trim();
          const headingMarkdown = /^#{1,6}\s+.+$/m.test(prevTrim);
          if (isBlockStart(next) || isBlockEnd(prev) || headingMarkdown) {
            return "";
          }
          return "";
        }
        // Don't wrap if it's already an HTML element (starts with <)
        if (part.trim().startsWith("<")) {
          return part;
        }
        // Don't wrap if it's empty
        if (!part.trim()) {
          // Preserve empty lines as vertical spacing between blocks
          return "";
        }
        // Wrap regular text in paragraph tags, preserving single newlines as <br>
        const withBreaks = part.replace(/\n/g, "<br>");
        return `<p class="mb-2">${withBreaks}</p>`;
      })
      // keep spacing divs and paragraphs; drop only truly empty strings
      .filter((line) => line !== "")
      .join("\n");

    // Sanitize the final HTML output to prevent XSS attacks
    return DOMPurify.sanitize(wrappedContent, {
      ALLOWED_TAGS: [
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "p",
        "br",
        "strong",
        "b",
        "em",
        "i",
        "u",
        "span",
        "ul",
        "ol",
        "li",
        "div",
        "a",
        "code",
        "input",
      ],
      ALLOWED_ATTR: [
        "class",
        "href",
        "target",
        "rel",
        "style",
        "type",
        "checked",
      ],
      ALLOW_DATA_ATTR: false,
    });
  };

  const style = brandColor
    ? ({ "--brand-color": brandColor } as React.CSSProperties)
    : undefined;

  return (
    <div className={`ai-content-renderer ${className}`} style={style}>
      {renderContent()}
    </div>
  );
};
